import DiffMatchPatch from "diff-match-patch";
import ExcelJS, { Alignment } from "exceljs";
import { useCallback, useMemo } from "react";
import { IClause } from "../interfaces";
import { Tag } from "../models/tag";
import { getRedlineTextForExcel } from "../pages/SuggestionResult/helper";
import { useAppData } from "./useAppData";

export const useExport = (appId: string, filteredSuggestions: IClause[], searchText: string, tags: Tag[]) => {
  const [redlined] = useAppData<boolean>(appId, "redlined");
  const [workspaceId] = useAppData<string>(appId, "workspaceId");
  const [, setIsExportModalOpen] = useAppData<boolean>(appId, "isExportModalOpen");
  const [, setIsExporting] = useAppData<boolean>(appId, "isExporting");

  const tagIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    tags?.forEach((tag) => {
      map[tag.tagId] = tag.name;
    });
    return map;
  }, [tags]);
  // --- helpers ---

  // 🔹 computed once, shared in the hook
  const isCharLimitExceeded = useMemo(() => {
    const charLimit = 32767;

    return filteredSuggestions.some((s) => {
      const textLength = s.clause_text.length;
      return redlined ? textLength + searchText.length > charLimit : textLength > charLimit;
    });
  }, [filteredSuggestions, searchText, redlined]);

  const getColumnsWidth = (index: number) => {
    if (index === 0) return 10; // ClauseId
    if (index === 1) return 30; // Title
    if (index === 2) return 80; // Text
    return 25;
  };

  const columnsStyle = (index: number): Partial<Alignment> => {
    if (index === 2) {
      return { vertical: "top", horizontal: "left", wrapText: true };
    }
    return { vertical: "middle", horizontal: "center", wrapText: true };
  };

  function initSheet(sheet: ExcelJS.Worksheet) {
    const columns = ["ClauseId", "Title", "Text", "Similarity", "Endorsed", "Documents", "Tags"];

    sheet.views = [{ zoomScale: 85 }];
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columns.length },
    };
    sheet.columns = columns.map((col, idx) => ({
      header: col,
      key: col,
      width: getColumnsWidth(idx),
    }));

    return columns;
  }

  function addHeaderStyles(sheet: ExcelJS.Worksheet) {
    const headerRow = sheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { color: { argb: "FFFFFF" }, bold: true, name: "Arial" };
      cell.alignment = { vertical: "bottom", horizontal: "center", wrapText: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "00004B" } };
    });
    headerRow.height = 30;
  }

  function addDataRows(sheet: ExcelJS.Worksheet, suggestions: IClause[]) {
    suggestions.forEach((s) => {
      sheet.addRow([
        s.clause_id,
        s.title,
        s.clause_text,
        typeof s.rankedSimilarity === "number"
          ? s.rankedSimilarity * 100
          : Number(s.rankedSimilarity || s.similarity || 0) * 100,
        s.is_endorsed ? "Yes" : "No",
        (s.sourceDocuments || [])
          .map((d) => d.doc_title || d.documentTitle || d.doc_file_name || d.id || "")
          .filter(Boolean)
          .join("; "),
        (s.mergedTagIds || []).map((id) => tagIdToNameMap[id] || id).join(", "),
      ]);
    });
  }

  function formatRows(
    sheet: ExcelJS.Worksheet,
    columns: string[],
    suggestions: IClause[],
    searchText: string,
    redlined: boolean
  ) {
    sheet.eachRow((row, rowIndex) => {
      if (rowIndex === 1) return;

      columns.forEach((_, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.alignment = columnsStyle(colIndex);
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.font = { name: "Arial" };
      });

      if (redlined) {
        const suggestion = suggestions[rowIndex - 2];
        const textCell = row.getCell(3);

        const dmp = new DiffMatchPatch();
        const diffs = dmp.diff_main(searchText, suggestion.clause_text);
        dmp.diff_cleanupSemantic(diffs);

        textCell.value = {
          richText: getRedlineTextForExcel(searchText, suggestion.clause_text),
        };
      }
    });
  }

  async function saveWorkbook(workbook: ExcelJS.Workbook, workspaceId: string) {
    const filename = `Suggestions_${workspaceId}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  const exportToExcel = useCallback(
    async (downloadAnyway?: boolean) => {
      if (!filteredSuggestions?.length) return;

      if (isCharLimitExceeded && !downloadAnyway) {
        setIsExportModalOpen(true);
        return;
      }

      setIsExporting(true);

      // use setTimeout to avoid blocking UI thread and schedules the export function as a new macro task
      setTimeout(async () => {
        try {
          const workbook = new ExcelJS.Workbook();
          const sheet = workbook.addWorksheet("Suggestions");
          const columns = initSheet(sheet);

          addHeaderStyles(sheet);
          addDataRows(sheet, filteredSuggestions);
          formatRows(sheet, columns, filteredSuggestions, searchText, redlined);

          await saveWorkbook(workbook, workspaceId);
        } finally {
          setIsExporting(false);
        }
      }, 0);
    },
    [filteredSuggestions, workspaceId, searchText, redlined, isCharLimitExceeded, tags]
  );

  return { isCharLimitExceeded, exportToExcel };
};
